import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000/api';

const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API_BASE,
  });

  // Auto-unwrap backend response format {success: true, data: X} -> X
  client.interceptors.response.use(
    (response) => {
      if (response.data && response.data.success !== undefined && response.data.data !== undefined) {
        response.data = response.data.data;
      }
      return response;
    },
    (error) => Promise.reject(error)
  );

  if (token) {
    client.defaults.headers.Authorization = `Bearer ${token}`;
  }

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const getApi = () => {
  const token = localStorage.getItem('token');
  return createApiClient(token);
};

const api = {
  auth: {
    login: async (email, password) => {
      const response = await getApi().post('/auth/login', { email, password });
      return response.data;
    },
    me: async () => {
      const response = await getApi().get('/auth/me');
      return response.data;
    },
    changePassword: async (oldPassword, newPassword) => {
      const response = await getApi().post('/auth/change-password', {
        oldPassword,
        newPassword,
      });
      return response.data;
    },
  },

  cases: {
    list: async (params) => {
      const response = await getApi().get('/cases', { params });
      return response.data;
    },
    get: async (id) => {
      const response = await getApi().get(`/cases/${id}`);
      return response.data;
    },
    create: async (data) => {
      const response = await getApi().post('/cases', data);
      return response.data;
    },
    update: async (id, data) => {
      const response = await getApi().put(`/cases/${id}`, data);
      return response.data;
    },
    updateStatus: async (id, statusData) => {
      const response = await getApi().put(`/cases/${id}/status`, statusData);
      return response.data;
    },
    getStats: async () => {
      const response = await getApi().get('/cases/stats');
      return response.data;
    },
    getHistory: async (id) => {
      const response = await getApi().get(`/cases/${id}/history`);
      return response.data;
    },
    delete: async (id) => {
      const response = await getApi().delete(`/cases/${id}`);
      return response.data;
    },
  },

  documents: {
    list: async (caseId) => {
      const response = await getApi().get(`/cases/${caseId}/documents`);
      return response.data;
    },
    upload: async (caseId, files, documentType) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('document_type', documentType);

      const response = await getApi().post(
        `/cases/${caseId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    download: async (documentId) => {
      const response = await getApi().get(`/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },
    delete: async (documentId) => {
      const response = await getApi().delete(`/documents/${documentId}`);
      return response.data;
    },
  },

  messages: {
    list: async (caseId) => {
      const response = await getApi().get(`/cases/${caseId}/messages`);
      return response.data;
    },
    send: async (caseId, message, attachment) => {
      const formData = new FormData();
      formData.append('message', message);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await getApi().post(`/cases/${caseId}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    markRead: async (messageId) => {
      const response = await getApi().patch(`/messages/${messageId}/read`);
      return response.data;
    },
    markAllRead: async (caseId) => {
      const response = await getApi().patch(`/cases/${caseId}/messages/read`);
      return response.data;
    },
    getCanned: async () => {
      const response = await getApi().get('/messages/canned');
      return response.data;
    },
  },

  followups: {
    list: async (caseId) => {
      const response = await getApi().get(`/cases/${caseId}/followups`);
      return response.data;
    },
    create: async (caseId, data) => {
      const response = await getApi().post(`/cases/${caseId}/followups`, data);
      return response.data;
    },
    complete: async (id) => {
      const response = await getApi().patch(`/followups/${id}/complete`);
      return response.data;
    },
  },

  users: {
    list: async () => {
      const response = await getApi().get('/users');
      return response.data;
    },
    create: async (data) => {
      const response = await getApi().post('/users', data);
      return response.data;
    },
    update: async (id, data) => {
      const response = await getApi().put(`/users/${id}`, data);
      return response.data;
    },
    toggleActive: async (id) => {
      const response = await getApi().patch(`/users/${id}/toggle-active`);
      return response.data;
    },
    resetPassword: async (id) => {
      const response = await getApi().post(`/users/${id}/reset-password`);
      return response.data;
    },
  },

  universities: {
    list: async () => {
      const response = await getApi().get('/universities');
      return response.data;
    },
    create: async (data) => {
      const response = await getApi().post('/universities', data);
      return response.data;
    },
    update: async (id, data) => {
      const response = await getApi().put(`/universities/${id}`, data);
      return response.data;
    },
    toggleActive: async (id) => {
      const response = await getApi().patch(`/universities/${id}/toggle-active`);
      return response.data;
    },
  },


  applyForOptions: {
    list: async () => {
      const response = await getApi().get('/apply-for-options');
      return response.data;
    },
    active: async () => {
      const response = await getApi().get('/apply-for-options/active');
      return response.data;
    },
    create: async (data) => {
      const response = await getApi().post('/apply-for-options', data);
      return response.data;
    },
    update: async (id, data) => {
      const response = await getApi().put('/apply-for-options/' + id, data);
      return response.data;
    },
    delete: async (id) => {
      const response = await getApi().delete('/apply-for-options/' + id);
      return response.data;
    },
  },
  notifications: {
    list: async () => {
      const response = await getApi().get('/notifications');
      return response.data;
    },
    unreadCount: async () => {
      const response = await getApi().get('/notifications/unread-count');
      return response.data;
    },
    markRead: async (id) => {
      const response = await getApi().patch(`/notifications/${id}/read`);
      return response.data;
    },
    markAllRead: async () => {
      const response = await getApi().patch('/notifications/read-all');
      return response.data;
    },
  },
};

// Expose raw axios methods for direct API calls
api.get = async (url, config) => { const response = await getApi().get(url, config); return response; };
api.post = async (url, data, config) => { const response = await getApi().post(url, data, config); return response; };
api.put = async (url, data, config) => { const response = await getApi().put(url, data, config); return response; };
api.patch = async (url, data, config) => { const response = await getApi().patch(url, data, config); return response; };
api.delete = async (url, config) => { const response = await getApi().delete(url, config); return response; };

export default api;
